import React, {useEffect} from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import NewTermsForm from './components/NewTermsForm';
import eulaActions from '../../actions/eula.actions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../..';
import {useGetMessages, useIntersection} from '../../hooks';
import {Eula} from '../../reducers/eula.reducer';
import {selectAffiliateAgreement} from '../../selectors';
import {Message} from 'epic-diesel-common';
import {Affiliate} from '../../../shared/types';
import {getAgreementSystem} from '../../../shared/utils/AffiliateUtils';
import {dateFormatUTC} from '../../../shared/utils/DateUtils';
import {EulaItem, HeaderSkeleton, ParagraphSkeleton, DownloadButton} from '../../components';
import useStyles, {Typography, Sidebar, Content, Navbar} from './NewTerms.styles';

export interface NewTermsProps {
    affiliate: Affiliate;
}

const NewTerms: React.FC<NewTermsProps> = ({affiliate}) => {
    const styles = useStyles();
    const dispatch = useDispatch();
    const agreement = useSelector<RootState, Eula>(selectAffiliateAgreement);
    const system = getAgreementSystem(affiliate);
    const {migrateBy, releasedAt} = system || {};
    const [ref, entry] = useIntersection();
    const isRendering = agreement.loading || !agreement.body;

    useEffect(() => {
        dispatch(eulaActions.getAffiliateAgreement());
    }, []);

    const messages = useGetMessages(
        {
            downloadPDF: 'epic.affiliate.common.downloadPDF',
            acceptRequired: 'epic.affiliate.tos.updateRequireAccept.v2',
            acceptRequiredBody1: {
                code: 'epic.affiliate.tos.maintainMembership.v2',
                args: [dateFormatUTC(migrateBy)]
            },
            acceptRequiredBody2: 'epic.affiliate.tos.ageRequirement.v2',
            acceptRequiredBody3: {
                code: 'epic.affiliate.tos.acceptTimeLimit.v2',
                args: [dateFormatUTC(migrateBy)]
            },
            acceptRequiredBody4: {
                code: 'epic.affiliate.common.note',
                args: [
                    <Message
                        key="warningNote"
                        code="epic.affiliate.tos.warningNote.v2"
                        args={[dateFormatUTC(releasedAt)]}
                    />
                ]
            }
        },
        [migrateBy, releasedAt]
    );

    const sections = [
        isRendering ? (
            <>
                <HeaderSkeleton variant="h4" height={50} />
                <ParagraphSkeleton />
                <ParagraphSkeleton type="sma" />
            </>
        ) : (
            <>
                <Typography variant="h4" color="textPrimary">
                    {messages.acceptRequired}
                </Typography>
                <Typography component="div" variant="body2" color="textSecondary">
                    <p>{messages.acceptRequiredBody1}</p>
                    <p>{messages.acceptRequiredBody2}</p>
                    <p>{messages.acceptRequiredBody3}</p>
                    <p>{messages.acceptRequiredBody4}</p>
                </Typography>
            </>
        ),
        <>
            <EulaItem isLoading={isRendering} item={agreement} />
        </>
    ];

    return (
        <Grid container className={styles.root}>
            <Navbar>
                <DownloadButton loading={isRendering} color="secondary" to={agreement.url}>
                    {messages.downloadPDF}
                </DownloadButton>
            </Navbar>
            <Content>
                {sections.map((section, i) => (
                    <Box key={i}>
                        {section}
                        {i + 1 < sections.length && <Divider className={styles.divider} />}
                    </Box>
                ))}
                <Divider className={styles.divider} innerRef={ref} />
            </Content>
            <Sidebar>
                <NewTermsForm
                    className={styles.sidebarForm}
                    agreement={agreement}
                    migrateBy={migrateBy}
                    isRendering={isRendering}
                    scrolledToBottom={entry.isIntersecting}
                />
            </Sidebar>
        </Grid>
    );
};

export default NewTerms;
